/** Angular Imports */
import { isPlatformServer } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { map } from 'rxjs';

/** Local Imports */
import { AuthService } from '@core/services/auth/auth.service';

/**
 * Detects whether the current navigation is an Auth0 redirect to `/login`
 * (verify success, link-used, already-verified, access-expired, error,
 * password-reset success, etc.). These must always reach LoginComponent
 * so it can process the redirect for the *intended* user — not be
 * short-circuited to `/dashboard` by an unrelated existing session
 * (e.g. admin opening a dealer's reset link).
 *
 * `iss` (OIDC issuer) is included because Auth0's password-reset success
 * redirect carries only `?iss=https%3A%2F%2F<tenant>.auth0.com%2F` and
 * no other markers.
 *
 * @returns True if any Auth0 redirect markers are present in the current URL.
 */
function isAuth0Redirect(router: Router): boolean {
    const url = router.getCurrentNavigation()?.extractedUrl ?? router.parseUrl(router.url);
    const qp = url.queryParamMap;
    return (
        qp.has('success') || qp.has('code') || qp.has('message') || qp.has('error') || qp.has('iss')
    );
}

/**
 * Route guard that allows access only to unauthenticated users.
 *
 * @param route - The activated route snapshot
 * @param segments - The URL segments
 * @returns {boolean | UrlTree | Observable<boolean | UrlTree>} True if user is NOT authenticated, otherwise redirects to root
 *
 * @remarks
 * This is a functional guard (Angular 15+) used with `canMatch`.
 * Prevents authenticated users from accessing public pages like login.
 * Redirects authenticated users to dashboard.
 */
export const guestGuard: CanMatchFn = (route, segments) => {
    /**
     * Authentication service instance
     * @type {AuthService}
     */
    const authService: AuthService = inject(AuthService);

    /**
     * Router service for navigation
     * @type {Router}
     */
    const router: Router = inject(Router);

    const platformId = inject(PLATFORM_ID);

    // On the server, we don't know the auth state. Return false so we fall back
    // to the CheckingSessionComponent and avoid a flash of the login page for auth'd users.
    if (isPlatformServer(platformId)) {
        return false;
    }

    // Auth0 redirect to `/login` (verify success/expiry/error/password reset/etc.):
    // the URL is meant for the user named in the link, not whoever is currently
    // signed in. Always let LoginComponent run so it can decide between a soft
    // local clear and a full BFF logout — clearing here would hide the prior
    // auth state from that decision and leak the HttpOnly session cookie.
    if (isAuth0Redirect(router)) {
        return true;
    }

    const isAuthenticated = authService.checkAuthStatus();

    if (isAuthenticated) {
        return router.createUrlTree(['/dashboard']);
    }

    // Verify with backend in case the user has a valid HttpOnly cookie but no sessionStorage (e.g., new tab)
    return authService.verifyAuthWithBackend().pipe(
        map((isAuth) => {
            if (isAuth) {
                return router.createUrlTree(['/dashboard']);
            }
            return true;
        }),
    );
};
